/**
 * O cartão "ID do negócio" com Copiar (11.4) — o código humano do AD-12.
 *
 * É o número que a vendedora digita no "Exportar" do Carga Máquina (FR-72, Fluxo 1, passo 7): em
 * JetBrains Mono, grande, e com um botão que o põe na área de transferência. A confirmação é um
 * **toast** (DESIGN.md): navy, centrado embaixo, some sozinho — e nunca é a única confirmação de
 * erro: a falha da área de transferência (permissão negada, contexto sem HTTPS) sai escrita no
 * cartão.
 */

import { useEffect, useState } from 'react'

import { ORCAMENTO_SO_DEPOIS_DA_EMPRESA } from './apresentacao'
import { IconeDeCopiar } from './copiar'
import './id-do-negocio.css'

/** Quanto tempo o toast fica na tela. */
const DURACAO_DO_TOAST_EM_MS = 2500

export const ID_COPIADO = 'ID copiado.'
export const COPIA_FALHOU =
  'Não foi possível copiar. Selecione o número e copie à mão.'

/**
 * `semEmpresa` (13.20b2): o código do Negócio sem Empresa ainda não recebe orçamento — o lançado
 * com ele no Carga Máquina é recusado até a Empresa ser definida (e reprocessado na definição). O
 * cartão diz isso junto do código, e não só depois de copiar.
 */
export function IdDoNegocio({
  codigo,
  semEmpresa = false,
}: {
  codigo: number
  semEmpresa?: boolean
}) {
  /* Cada cópia bem-sucedida é um número novo: o efeito reinicia o temporizador a cada clique. */
  const [copias, setCopias] = useState(0)
  const [copiado, setCopiado] = useState(false)
  const [falhou, setFalhou] = useState(false)

  useEffect(() => {
    if (copias === 0) {
      return
    }
    const temporizador = window.setTimeout(() => {
      setCopiado(false)
    }, DURACAO_DO_TOAST_EM_MS)
    return () => {
      window.clearTimeout(temporizador)
    }
  }, [copias])

  const copiar = () => {
    setFalhou(false)
    const areaDeTransferencia = navigator.clipboard as Clipboard | undefined
    if (areaDeTransferencia === undefined) {
      setFalhou(true)
      return
    }
    areaDeTransferencia.writeText(String(codigo)).then(
      () => {
        setCopiado(true)
        setCopias((vezes) => vezes + 1)
      },
      () => {
        setFalhou(true)
      },
    )
  }

  return (
    <section className="ficha__card" aria-label="ID do negócio">
      <div className="ficha__card-topo">
        <span className="ficha__card-titulo">ID do negócio</span>
        <span className="ficha__card-nota">Digite no “Exportar” do Carga Máquina</span>
      </div>
      <div className="ficha__card-corpo id-do-negocio">
        <span className="id-do-negocio__codigo numero">{codigo}</span>
        <button type="button" className="ficha__botao ficha__botao--copiar" onClick={copiar}>
          <IconeDeCopiar />
          Copiar
        </button>
      </div>
      {semEmpresa ? (
        <p className="ficha__card-nota id-do-negocio__nota">{ORCAMENTO_SO_DEPOIS_DA_EMPRESA}</p>
      ) : null}
      {falhou ? (
        <p className="ficha__erro id-do-negocio__erro" role="alert">
          {COPIA_FALHOU}
        </p>
      ) : null}
      {copiado ? (
        <div className="toast" role="status">
          {ID_COPIADO}
        </div>
      ) : null}
    </section>
  )
}
